import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MoreHorizontal, Play, Square, Clock, CheckCircle, XCircle, Calendar, Eye, Loader2, Trash2, RefreshCw } from "lucide-react";
import { Button } from "@repo/ui/button";
import { Badge } from "@repo/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@repo/ui/alert-dialog";
import { cn } from "@repo/ui/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useJobRealtime } from "@/hooks/useJobRealtime";
import { useJobRunCounts } from "@/hooks/useJobRunCounts";
import {
  runJob,
  deleteJob,
  cancelJobRun,
  formatJobGeography,
  getJobAdapterNames,
  isMultiAdapterJob,
} from "@/lib/api";
import type { JobWithRelations } from "@/lib/api";
import { formatDate } from "@/lib/formatDate";
import { JobDetailsDialog } from "./JobDetailsDialog";

type JobStatus = "idle" | "running" | "scheduled" | "completed" | "failed" | "cancelled" | "paused";

interface JobsTableProps {
  filter: "all" | "running" | "scheduled" | "completed";
  searchQuery: string;
  openJobId?: string;
}

const statusConfig: Record<JobStatus, { icon: typeof Play; label: string; className: string }> = {
  idle: { icon: Clock, label: "Idle", className: "bg-muted text-muted-foreground" },
  running: { icon: Play, label: "Running", className: "status-running" },
  scheduled: { icon: Calendar, label: "Scheduled", className: "status-pending" },
  completed: { icon: CheckCircle, label: "Completed", className: "status-completed" },
  failed: { icon: XCircle, label: "Failed", className: "status-failed" },
  cancelled: { icon: Square, label: "Cancelled", className: "bg-muted text-muted-foreground" },
  paused: { icon: Clock, label: "Paused", className: "bg-warning/15 text-warning border-warning/30" },
};

/**
 * Per-active-run progress cell. Live counts only — the cached
 * `job_runs.urls_discovered/scraped` columns were silently truncated by the
 * pre-fix reconciler's 1000-row PostgREST limit and produced impossible
 * states (e.g. discovered=0, scraped=1895). Brief loading flicker is
 * preferable to a contradictory number.
 */
function ActiveRunProgress({
  runId,
  isPaused,
}: {
  runId: string;
  isPaused: boolean;
}) {
  const counts = useJobRunCounts(runId);
  const discovered = counts.total;
  const scraped = counts.completed;
  const pct = discovered > 0 ? Math.round((scraped / Math.max(discovered, 1)) * 100) : 0;
  return (
    <div className="text-xs">
      {discovered > 0 ? (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden min-w-[60px]">
            <div
              className="h-full bg-accent transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-accent font-medium whitespace-nowrap">
            {scraped}/{discovered}
          </span>
        </div>
      ) : (
        <span className="text-accent font-medium animate-pulse">
          {isPaused ? "Paused" : "Discovering..."}
        </span>
      )}
    </div>
  );
}

export const JobsTable = ({ filter, searchQuery, openJobId }: JobsTableProps) => {
  const { canManageJobs, isViewer } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { jobs, activeRuns, isLoading, refetch } = useJobRealtime();

  const [runningJobId, setRunningJobId] = useState<string | null>(null);
  const [cancellingRunId, setCancellingRunId] = useState<string | null>(null);
  const [deleteJobId, setDeleteJobId] = useState<string | null>(null);
  const [forceRecrawlJobId, setForceRecrawlJobId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobWithRelations | null>(null);

  // Dialog open state is now derived from the URL: when openJobId is set
  // (route /dashboard/jobs/:jobId) the dialog shows, otherwise it doesn't.
  // This makes browser Back work — returning from a run-detail page lands
  // on /dashboard/jobs/:jobId and the dialog re-mounts automatically.
  const detailsOpen = Boolean(openJobId && selectedJob);

  const openJobDialog = useCallback(
    (job: JobWithRelations) => {
      setSelectedJob(job);
      navigate(`/dashboard/jobs/${job.id}`);
    },
    [navigate],
  );

  const closeJobDialog = useCallback(
    (open: boolean) => {
      if (open) return;
      setSelectedJob(null);
      navigate("/dashboard/jobs");
    },
    [navigate],
  );

  useEffect(() => {
    if (!openJobId || !jobs.length) return;
    const matched = jobs.find((job) => job.id === openJobId);
    if (matched) {
      setSelectedJob(matched);
    }
  }, [openJobId, jobs]);

  const handleRunJob = async (jobId: string, forceRecrawl = false) => {
    try {
      setRunningJobId(jobId);
      toast({
        title: "Starting Job",
        description: forceRecrawl
          ? "The job is now running and will refetch sales we already hold..."
          : "The job is now running..."
      });

      const result = await runJob(jobId, forceRecrawl ? { forceRecrawl: true } : {});
      
      if (result.success) {
      toast({
        title: "Job Started",
        description: "Job is now running. Progress will update automatically."
      });
      // No need to manually refresh - realtime will handle it
    } else {
      toast({
        title: "Job Failed",
        description: result.error || "Unknown error",
        variant: "destructive"
      });
    }
  } catch (error) {
    console.error('Run job error:', error);
      toast({
        title: "Error",
        description: "Failed to run job",
        variant: "destructive"
      });
    } finally {
      setRunningJobId(null);
    }
  };

  const handleCancelRun = async (runId: string, jobName?: string) => {
    try {
      setCancellingRunId(runId);
      const result = await cancelJobRun(runId);

      if (result.success) {
        toast({
          title: "Job Cancelled",
          description: jobName ? `${jobName} has been cancelled.` : "The job run has been cancelled.",
        });
      } else {
        toast({
          title: "Cancel Failed",
          description: result.error || "Failed to cancel the job run.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Cancel job run error:", error);
      toast({
        title: "Cancel Failed",
        description: "Failed to cancel the job run.",
        variant: "destructive",
      });
    } finally {
      setCancellingRunId(null);
    }
  };

  const handleDeleteJob = async () => {
    if (!deleteJobId) return;
    
    try {
      setIsDeleting(true);
      await deleteJob(deleteJobId);
      toast({
        title: "Job Deleted",
        description: "The job has been deleted"
      });
      refetch();
    } catch (error) {
      console.error('Delete job error:', error);
      toast({
        title: "Error",
        description: "Failed to delete job",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
      setDeleteJobId(null);
    }
  };

  // Apply filters - check both job status and active runs
  let filteredJobs = jobs;

  if (filter === "running") {
    filteredJobs = filteredJobs.filter((j) => j.status === "running" || activeRuns.has(j.id));
  } else if (filter === "scheduled") {
    filteredJobs = filteredJobs.filter((j) => j.status === "scheduled");
  } else if (filter === "completed") {
    filteredJobs = filteredJobs.filter((j) => j.status === "completed" || j.status === "failed");
  }

  // Apply search
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filteredJobs = filteredJobs.filter((j) => {
      if (j.name.toLowerCase().includes(query)) return true;
      // Search in adapter names (supports both single and multi-adapter)
      const adapterNames = getJobAdapterNames(j);
      return adapterNames.some(name => name.toLowerCase().includes(query));
    });
  }

  if (isLoading) {
    return (
      <div className="p-12 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (filteredJobs.length === 0) {
    return (
      <div className="p-12 text-center text-muted-foreground">
        <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <p className="font-medium">No jobs found</p>
        <p className="text-sm">
          {canManageJobs ? "Create a new job to get started" : "No jobs available to view"}
        </p>
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Job Name</TableHead>
            <TableHead>Adapter</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Schedule</TableHead>
            <TableHead>Last Run</TableHead>
            <TableHead>Config</TableHead>
            <TableHead className="w-10"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredJobs.map((job) => {
            const hasActiveRun = activeRuns.has(job.id);
            const activeRun = activeRuns.get(job.id);
            // Check for paused phase in active run
            const isPaused = activeRun?.metadata?.phase === 'paused' || (activeRun as any)?.phase === 'paused';
            // When job.status is "running" but there's no active job_run,
            // the cached status is stale (a scheduled job between fires,
            // or a finished run whose end didn't propagate). Fall back to
            // a derived "idle/scheduled" so the UI doesn't pretend there's
            // work in progress when there isn't.
            const jobStatusLooksRunning = job.status === 'running' || job.status === 'scheduled';
            const effectiveStatus = isPaused
              ? 'paused'
              : hasActiveRun
                ? 'running'
                : jobStatusLooksRunning && job.cron_expression
                  ? 'scheduled'
                  : jobStatusLooksRunning
                    ? 'idle'
                    : job.status;
            const status = statusConfig[effectiveStatus as JobStatus] || statusConfig.idle;
            const StatusIcon = status.icon;
            const isRunning = runningJobId === job.id || (hasActiveRun && !isPaused);
            const config = job.config;
            
            // Get adapter names (supports both single and multi-adapter patterns)
            const adapterNames = getJobAdapterNames(job);
            const geographyLabel = formatJobGeography(job);
            const isMultiAdapter = isMultiAdapterJob(job);

            return (
              <TableRow
                key={job.id}
                className="cursor-pointer"
                onClick={() => openJobDialog(job)}
              >
                <TableCell className="font-medium">{job.name}</TableCell>
                <TableCell>
                  {adapterNames.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {adapterNames.slice(0, 2).map((name) => (
                        <Badge key={name} variant="outline" className="font-normal">
                          {name}
                        </Badge>
                      ))}
                      {adapterNames.length > 2 && (
                        <Badge variant="outline" className="font-normal text-muted-foreground">
                          +{adapterNames.length - 2}
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <Badge variant="outline" className="font-normal text-muted-foreground">
                      No adapter
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Badge className={cn("gap-1", status.className)}>
                    {isRunning ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <StatusIcon className="w-3 h-3" />
                    )}
                    {isRunning ? 'Running' : status.label}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {job.cron_expression || "—"}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDate(job.last_run_at, { includeTime: true, includeYear: false })}
                </TableCell>
                <TableCell>
                  {activeRun ? (
                    <ActiveRunProgress
                      runId={(activeRun as { id: string }).id}
                      isPaused={isPaused}
                    />
                  ) : isMultiAdapter ? (
                    <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                      {geographyLabel}
                      {config?.timeRange && ` · ${config.timeRange.replace(/-/g, " ")}`}
                    </div>
                  ) : config ? (
                    <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                      {config.listingUrls?.length 
                        ? `${config.listingUrls.length} URL${config.listingUrls.length !== 1 ? 's' : ''}`
                        : 'No URLs configured'}
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">—</div>
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      asChild
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openJobDialog(job)}>
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      
                      {canManageJobs && (
                        <>
                          <DropdownMenuSeparator />
                          {effectiveStatus !== 'running' && (
                            <DropdownMenuItem 
                              onClick={() => handleRunJob(job.id)}
                              disabled={isRunning}
                            >
                              <Play className="w-4 h-4 mr-2" />
                              Run Now
                            </DropdownMenuItem>
                          )}
                          {effectiveStatus !== 'running' && (
                            <DropdownMenuItem
                              onClick={() => setForceRecrawlJobId(job.id)}
                              disabled={isRunning}
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Run Now (force recrawl)
                            </DropdownMenuItem>
                          )}
                          {effectiveStatus === 'running' && activeRun && (
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleCancelRun(activeRun.id, job.name)}
                              disabled={cancellingRunId === activeRun.id}
                            >
                              {cancellingRunId === activeRun.id ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Square className="w-4 h-4 mr-2" />
                              )}
                              Stop Job
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-destructive"
                            onClick={() => setDeleteJobId(job.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                      
                      {isViewer && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                            Read-only access
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <AlertDialog open={!!deleteJobId} onOpenChange={() => setDeleteJobId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Job</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this job? All associated run history and scraped data will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteJob}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!forceRecrawlJobId}
        onOpenChange={(open) => !open && setForceRecrawlJobId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Run now and force a recrawl?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  This run fetches every discovered page again, including sales we already hold
                  with a price and date. Use it when stored pages or values were lost, damaged or
                  poisoned. Each refetch costs provider credit.
                </p>
                <p>
                  A refetch saves a new snapshot and fills in what the page shows. It does not
                  clear a stored value the page lacks, so clean poisoned values in Poisoned Data
                  first. Pages already queued in another active run are still skipped.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (forceRecrawlJobId) void handleRunJob(forceRecrawlJobId, true);
                setForceRecrawlJobId(null);
              }}
            >
              Run with force recrawl
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <JobDetailsDialog
        open={detailsOpen}
        onOpenChange={closeJobDialog}
        job={selectedJob}
        onJobUpdated={refetch}
      />
    </>
  );
};